import {Predicate, registerBrick} from 'olympe';
import {ReactBrick} from "@olympeio/core";
import {combineLatest} from "rxjs";
import React, {useEffect, useMemo, useState} from "react";
import Unit from "../../../models/Unit";
import Observation from "../../../models/Observation";
import Report from "../../../models/Report";
import System from "../../../models/System";
import Component from "../../../models/Component";

import TreeView from '@mui/lab/TreeView';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import InspectionEntity from "../../../models/InspectionEntity";
import TreeItem from '@mui/lab/TreeItem';
import { Checkbox } from '@mui/material';

export default class ReportSystemTree extends ReactBrick {

    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([$.observe('report'), $.observe('unit')]);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [report, unit] = props.values;
            const [checkedEntities, setCheckedEntities] = useState(new Set());

            const observations = useMemo(() => {
                return unit.follow(Unit.observationsRel).andReturn()
                    .follow(Observation.targetRel).andReturn()
                    .executeFromCache()
                    .reduce((map, [obs, entity]) => map.set(entity.getTag(), obs), new Map())
            }, []);

            // Build Full Tree Data, once, from System to inspections
            const treeData = useMemo(() => {
                const systems = unit.follow(Unit.systemsRel).sortBy(InspectionEntity.rankProp).executeFromCache();
                const getRootComponents = (system) => system.follow(System.componentsRel).sortBy(InspectionEntity.rankProp).executeFromCache();

                // Transform component data to tree item, and filter out component that are not available
                const fetchComponents = (components) => {
                    if (components.size() === 0) {
                        return null;
                    }

                    return components.map((component) => {
                        const subComponents = component
                            .follow(Component.subComponentsRel)
                            .sortBy(Component.rankProp)
                            .executeFromCache();

                        const children = subComponents.size() > 0
                            ? fetchComponents(subComponents)
                            : component
                                .follow(Component.inspectionsRel)
                                .sortBy(Component.rankProp)
                                .executeFromCache().map((inspection) => {
                                    return {
                                        name: inspection.getName(),
                                        tag: inspection.getTag(),
                                        observation: observations.get(inspection.getTag()),
                                    };
                                }).filter((inspection) => !!inspection?.observation);

                        return children?.length > 0 ? {
                            name: component.getName(),
                            tag: component.getTag(),
                            observation: observations.get(component.getTag()),
                            children
                        } : null;
                    }).filter((node) => !!node?.observation);
                };

                return systems.map((system) => {
                    const rootComponents = getRootComponents(system);
                    const systemChildren = fetchComponents(rootComponents);
                    return systemChildren?.length > 0 ? {
                        name: system.getName(),
                        tag: system.getTag(),
                        observation: observations.get(system.getTag()),
                        children: systemChildren
                    } : null;
                }).filter((system) => !!system?.observation);
            }, []);

            // Subscribe to all existing observations, for the current report
            useEffect(() => {
                const sub = unit.follow(Unit.observationsRel)
                    .follow(Observation.targetRel).andReturn()
                    .back()
                    .follow(Report.observationsRel.getInverse())
                    .filter(Predicate.in(report))
                    .observe($).subscribe((results) => {
                        const entities = results.reduce((map, entity) => map.add(entity.getTag()), new Set());
                        setCheckedEntities(entities);
                    });
                return () => sub.unsubscribe();
            }, []);

            const onCheckChange = (checkValue, nodes) => {
                $.set('triggered observations', nodes.map((node) => node.observation));
                $.trigger(checkValue ? 'add observations' : 'remove observations');
            };

            return (
                <>
                    <div className='tree-view-container'>
                        <div className='tree-view-inner'>
                            {treeData && (
                                <TreeView
                                    defaultCollapseIcon={<ArrowDropDownIcon color='primary' fontSize='large'/>}
                                    defaultExpandIcon={<ArrowRightIcon color='primary' fontSize='large'/>}
                                >
                                    {treeData.map((node) => (
                                        <TreeEntity
                                            key={node.tag}
                                            node={node}
                                            checkedComponents={checkedEntities}
                                            onCheckChange={onCheckChange}
                                        />
                                    ))}
                                </TreeView>
                            )}
                        </div>
                    </div>
                </>
            );
        };
    }
}

registerBrick('018adb2f7af325f8842e', ReportSystemTree);

const TreeEntity = ({ node, checkedComponents, onCheckChange }) => {

    // Get all the children of this node
    const childrenTags = useMemo(() => {
        const getAllChildren = (n) => n.children?.flatMap((child) => [child.tag].concat(getAllChildren(child))) ?? [];
        return getAllChildren(node);
    }, []);

    const selected = checkedComponents.has(node.tag);
    const partiallySelected = selected && childrenTags.some((child) => checkedComponents.has(child));
    const allSelectedChildren = childrenTags.length === 0 || (partiallySelected && childrenTags.every((child) => checkedComponents.has(child)));
    const fullySelected = selected && allSelectedChildren;

    return (
        <TreeItem
            key={node.tag}
            nodeId={node.tag}
            label={
                <>
                    <Checkbox
                        checked={fullySelected}
                        indeterminate={!fullySelected && partiallySelected}
                        indeterminateIcon={<IndeterminateCheckBoxIcon sx={{ color: 'orange' }} />}
                        sx={{ color: 'pink', '&.Mui-checked': { color: 'mediumaquamarine' } }}
                        onChange={(event) => onCheckChange(event.currentTarget.checked, [node])}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <div className={'node-title muiTreeItem-label-justified'}>
                        {node.name}
                    </div>
                </>
            }>
            {node.children
                ? node.children.map((childrenNode) => (
                    <TreeEntity
                        key={childrenNode.tag}
                        node={childrenNode}
                        checkedComponents={checkedComponents}
                        onCheckChange={onCheckChange}
                    />
                ))
                : null}
        </TreeItem>
    );
};
